// src/services/authService.ts

import axiosInstance from "../CustomAxios/axiosInstance"
import type { User } from "./users/userService"

// Define the response type from the login API
interface LoginResponse {
    status: string
    message: string
    access_token?: string
    token_type?: string
    expires_in?: number
    user?: {
        id: string
        name: string
        username?: string
        email?: string
        role?: string
    }
}

// Define the login credentials type
interface LoginCredentials {
    username: string
    password: string
}

// Define the recent account type
interface RecentAccount {
    id: string
    username: string
    name: string
    lastLogin: string
    profileImage?: string
}

// Request throttler to limit API calls
class RequestThrottler {
    private static instance: RequestThrottler
    private queue: Array<{
        request: () => Promise<any>
        resolve: (value: any) => void
        reject: (reason: any) => void
        endpoint: string
        priority: number
    }> = []
    private inProgress = false
    private lastRequestTime = 0
    private minRequestGap = 200 // Increased from 100ms to 200ms
    private requestsByEndpoint: Record<string, number> = {}
    private endpointCooldowns: Record<string, number> = {}
    private readonly endpointLimits: Record<string, number> = {
        default: 8, // Increased default limit
        "/entities/brands": 3,  
        "/entities/categories": 3,
        "/entities/subcategories": 3,
        "/store/store_header": 3,
        "/inventory/stockInventory": 3,
        "/products/stock/summary": 3,
        "/orders": 3,
    }
    private readonly cooldownPeriod = 15000 // Increased from 10s to 15s

    private constructor() {}

    public static getInstance(): RequestThrottler {
        if (!RequestThrottler.instance) {
            RequestThrottler.instance = new RequestThrottler()
        }
        return RequestThrottler.instance
    }

    public async enqueue<T>(endpoint: string, request: () => Promise<T>, priority = 1): Promise<T> {
        // Check if endpoint is on cooldown
        const now = Date.now()
        if (this.endpointCooldowns[endpoint] && now < this.endpointCooldowns[endpoint]) {
            console.warn(`Endpoint ${endpoint} is on cooldown until ${new Date(this.endpointCooldowns[endpoint])}`)
            return Promise.reject(new Error(`Too many requests to ${endpoint}. Please try again later.`))
        }

        // Increment request count for this endpoint
        this.requestsByEndpoint[endpoint] = (this.requestsByEndpoint[endpoint] || 0) + 1

        // Check if endpoint limit exceeded
        const limit = this.endpointLimits[endpoint] || this.endpointLimits.default
        if (this.requestsByEndpoint[endpoint] > limit) {
            // Put endpoint on cooldown
            this.endpointCooldowns[endpoint] = now + this.cooldownPeriod
            // Reset counter after cooldown
            setTimeout(() => {
                this.requestsByEndpoint[endpoint] = 0
                delete this.endpointCooldowns[endpoint]
            }, this.cooldownPeriod)

            console.warn(`Endpoint ${endpoint} rate limit exceeded. Setting cooldown for ${this.cooldownPeriod}ms`)
            return Promise.reject(new Error(`Rate limit exceeded for ${endpoint}. Please try again later.`))
        }

        return new Promise<T>((resolve, reject) => {
            // Add request to queue with priority
            this.queue.push({
                request: async () => {
                    try {
                        const result = await request()
                        return result
                    } catch (error) {
                        throw error
                    }
                },
                resolve,
                reject,
                endpoint,
                priority,
            })

            // Sort queue by priority (higher priority first)
            this.queue.sort((a, b) => b.priority - a.priority)

            // Process queue if not already in progress
            if (!this.inProgress) {
                this.processQueue()
            }
        })
    }

    private async processQueue() {
        this.inProgress = true

        while (this.queue.length > 0) {
            // Enforce minimum time between requests
            const now = Date.now()
            const timeSinceLastRequest = now - this.lastRequestTime
            if (timeSinceLastRequest < this.minRequestGap) {
                await new Promise((resolve) => setTimeout(resolve, this.minRequestGap - timeSinceLastRequest))
            }

            // Get next request (already sorted by priority)
            const nextRequest = this.queue.shift()
            if (nextRequest) {
                try {
                    this.lastRequestTime = Date.now()
                    const result = await nextRequest.request()
                    nextRequest.resolve(result)
                } catch (error) {
                    nextRequest.reject(error)

                    // If this was a rate limit error, pause processing for a moment
                    if (error instanceof Error && error.message.includes("Too many requests")) {
                        console.warn("Rate limit hit, pausing queue processing for 2 seconds")
                        await new Promise((resolve) => setTimeout(resolve, 2000))
                    }
                }
            }
        }

        this.inProgress = false
    }

    // Reset all throttles and queues (useful for logout)
    public reset() {
        this.queue = []
        this.inProgress = false
        this.requestsByEndpoint = {}
        this.endpointCooldowns = {}
    }
}

// API cache to prevent duplicate requests
class ApiCache {
    private static instance: ApiCache
    private cache: Map<string, { data: any; timestamp: number }> = new Map()
    private readonly defaultTtl = 60000 // 1 minute default cache time

    private constructor() {}

    public static getInstance(): ApiCache {
        if (!ApiCache.instance) {
            ApiCache.instance = new ApiCache()
        }
        return ApiCache.instance
    }

    public get(key: string): any | null {
        const cached = this.cache.get(key)
        if (!cached) return null

        // Check if expired
        if (Date.now() - cached.timestamp > this.defaultTtl) {
            this.cache.delete(key)
            return null
        }

        return cached.data
    }

    public set(key: string, data: any): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
        })
    }

    public delete(key: string): void {
        this.cache.delete(key)
    }

    public clear(): void {
        this.cache.clear()
    }
}

// Auth service class
class AuthService {
    // Get API URL from environment variables with fallback
    private API_URL = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}`
    private TOKEN_KEY = "auth_token"
    private USER_KEY = "auth_user"
    private TOKEN_EXPIRY_KEY = "auth_token_expiry"
    private RECENT_ACCOUNTS_KEY = "recent_accounts"
    private throttler = RequestThrottler.getInstance()
    private apiCache = ApiCache.getInstance()

    /**
     * Login user with username and password
     * @param credentials - User credentials (username and password)
     * @returns Promise with user data and token
     */
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        try {
            if (!credentials.username || credentials.username.trim() === "") {
                throw new Error("Username is required")
            }

                        
            // Use axios instance for login request (bypassing interceptors since no token yet)
            const response = await axiosInstance.post('/auth/login', credentials, {
                // Override default headers since we don't have a token yet
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            })

                        
            const data = response.data

            // Check for error status
            if (response.status >= 400 || data.status === "error") {
                const errorMessage = data.message || "Unknown error occurred"
                                throw new Error(errorMessage)
            }

            // Check for token in the correct field (access_token instead of token)
            if (data.access_token) {
                // Decode the token to extract role information
                const decodedToken = this.decodeToken(data.access_token)
                
                // Store token with expiry if provided
                this.setToken(data.access_token)
                if (data.expires_in) {
                    const expiryTime = Date.now() + data.expires_in * 1000
                    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString())
                }

                // Make sure we have user data
                if (data.user) {
                    // If the token contains a role but the user object doesn't, add it
                    if (decodedToken && decodedToken.role && !data.user.role) {
                        data.user.role = decodedToken.role
                                            }

                    this.setUser(data.user)

                    // Display current user name and role after successful login
                    const userName = data.user.name || data.user.username || "Unknown"
                    const userRole = data.user.role || (decodedToken ? decodedToken.role : "Unknown")
                    
                    // Store this account in recent accounts list
                    this.storeRecentAccount(credentials.username)
                }

                // Clear any caches/throttlers on new login
                this.apiCache.clear()
                this.throttler.reset()

                return data
            } else {
                                throw new Error("Authentication failed: Token missing from response")
            }
        } catch (error: any) {
            
            // Handle axios errors
            if (error.response) {
                const errorMessage = error.response.data?.message || "Authentication failed"
                                throw new Error(errorMessage)
            }

            // Add more context to the error for debugging
            if (error instanceof Error) {
                // Check if it's a network error
                if (error.message.includes("Network Error") ) {
                    throw new Error("Network error: Please check if the server is running and accessible")
                }

                // If it's the 500 error with token creation
                if (error.message.includes("Could not create token")) {
                    throw new Error(
                        "The server encountered an error while creating your authentication token. This is likely a server configuration issue with JWT. Please check your Laravel .env file for correct JWT_SECRET and JWT configuration.",
                    )
                }

                throw error
            }

            throw new Error("An unexpected error occurred during login")
        }
    }

    /**
     * Store a recently used account for the account switcher
     */
    storeRecentAccount(username?: string): void {
        const user = this.getUser()
        if (!user || !user.id) return

        try {
            // Get existing recent accounts
            const accountsStr = localStorage.getItem(this.RECENT_ACCOUNTS_KEY)
            let accounts: RecentAccount[] = []

            if (accountsStr) {
                accounts = JSON.parse(accountsStr)
            }

            // Create account entry
            const accountEntry: RecentAccount = {
                id: user.id,
                username: username || user.username || "",
                name: user.name || user.username || "User",
                lastLogin: new Date().toISOString(),
                profileImage: user.profileImage,
            }

            // Ensure username is stored
            if (!accountEntry.username && username) {
                accountEntry.username = username
            }

            // Remove this account if it already exists
            accounts = accounts.filter((acc) => acc.id !== user.id)

            // Add to the beginning of the array
            accounts.unshift(accountEntry)

            // Keep only the last 5 accounts
            accounts = accounts.slice(0, 5)

            // Save back to localStorage
            localStorage.setItem(this.RECENT_ACCOUNTS_KEY, JSON.stringify(accounts))
        } catch (err) {
                    }
    }

    /**
     * Get list of recently used accounts
     * @returns Array of recent accounts
     */
    getRecentAccounts(): RecentAccount[] {
        try {
            const accountsStr = localStorage.getItem(this.RECENT_ACCOUNTS_KEY)
            if (accountsStr) {
                const accounts = JSON.parse(accountsStr)

                // Fix: Add explicit type for the account parameter
                return accounts.filter((account: RecentAccount) => !!account.username)
            }
        } catch (err) {
                    }
        return []
    }

    /**
     * Decode JWT token to extract payload information
     * @param token - JWT token string
     * @returns Decoded token payload or null if invalid
     */
    decodeToken(token: string | null): any {
        if (!token) return null

        try {
            // JWT token consists of three parts: header.payload.signature
            const parts = token.split(".")
            if (parts.length !== 3) return null

            // Decode the payload (middle part)
            const payload = parts[1]
            const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")

            // Create proper padding
            const pad = base64.length % 4
            const paddedBase64 = pad ? base64 + "=".repeat(4 - pad) : base64

            // Decode base64
            const jsonPayload = atob(paddedBase64)

            // Parse JSON
            return JSON.parse(jsonPayload)
        } catch (error) {
                        return null
        }
    }

    /**
     * Get the current authentication token
     * @returns The stored auth token or null if not logged in
     */
    getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY)
    }

    /**
     * Set the authentication token
     * @param token - The token to store
     */
    private setToken(token: string): void {
        localStorage.setItem(this.TOKEN_KEY, token)
    }

    /**
     * Get the current authenticated user
     * @returns The stored user data or null if not logged in
     */
    getUser(): any {
        const userStr = localStorage.getItem(this.USER_KEY)
        if (userStr) {
            try {
                return JSON.parse(userStr)
            } catch (e) {
                                return null
            }
        }
        return null
    }

    /**
     * Set the user data
     * @param user - The user data to store
     */
    private setUser(user: any): void {
        localStorage.setItem(this.USER_KEY, JSON.stringify(user))
    }

    /**
     * Check if token is expired
     * @returns True if token is expired or expiry info is missing
     */
    isTokenExpired(): boolean {
        const expiryTimeStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY)
        if (!expiryTimeStr) {
            // If no expiry info, consider token might be expired
            return true
        }

        const expiryTime = Number.parseInt(expiryTimeStr, 10)
        return Date.now() > expiryTime
    }

    /**
     * Validate authentication status with server
     * @returns Promise resolving to true if authenticated, false otherwise
     */
    async validateAuth(): Promise<boolean> {
        try {
            // Skip validation if no token exists
            if (!this.getToken()) {
                return false
            }

            // Check token expiration if we have that info
            if (this.isTokenExpired()) {
                this.logout()
                return false
            }

            // Make a request to validate the token with the server using axios instance
            const response = await axiosInstance.get('/validate-token')
            return response.data && response.data.valid === true
        } catch (error) {
                        return false
        }
    }

    /**
     * Check if user is authenticated
     * @returns True if user is authenticated
     */
    isAuthenticated(): boolean {
        const token = this.getToken()
        const user = this.getUser()

        // Basic client-side validation
        if (!token || !user || !user.id) {
            return false
        }

        // Check token expiration if we have that info
        if (this.isTokenExpired()) {
            this.logout()
            return false
        }

        return true
    }

    /**
     * Logout the current user
     */
    logout(): void {
        localStorage.removeItem(this.TOKEN_KEY)
        localStorage.removeItem(this.USER_KEY)
        localStorage.removeItem(this.TOKEN_EXPIRY_KEY)

        // Clear caches and throttlers
        this.apiCache.clear()
        this.throttler.reset()
    }

    /**
     * Get authentication headers for API requests
     * @returns Headers object with Authorization token
     */
    getAuthHeaders(): HeadersInit {
        const token = this.getToken()
        return {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
        }
    }

    /**
     * Parse the URL to get the endpoint path for caching/throttling
     */
    private getEndpointPath(url: string): string {
        try {
            const parsedUrl = new URL(url)
            // Get only the path part for endpoint identification
            return parsedUrl.pathname
        } catch (e) {
            // If URL parsing fails, just return the original URL
            return url
        }
    }

    /**
     * Helper function for authenticated API requests with throttling and caching
     * @param url - The API URL (relative path)
     * @param options - Axios config options
     * @param useCache - Whether to use cache for this request
     * @returns Promise with response data
     */
    async fetchWithAuth(url: string, options: any = {}, useCache = false): Promise<any> {
        const endpoint = this.getEndpointPath(url)

        // Generate cache key
        const cacheKey = `${options.method || "GET"}-${url}-${JSON.stringify(options.data || "")}`

        // Check cache first if it's a GET request and useCache is true
        if (useCache && (!options.method || options.method === "GET")) {
            const cachedData = this.apiCache.get(cacheKey)
            if (cachedData) {
                                return cachedData
            }
        }

        // Use throttler to manage request rate
        return this.throttler.enqueue(
            endpoint,
            async () => {
                try {
                    const response = await axiosInstance({
                        url,
                        method: options.method || 'GET',
                        data: options.data,
                        params: options.params,
                        headers: {
                            ...options.headers,
                            "Cache-Control": "no-cache, no-store, must-revalidate",
                            "Pragma": "no-cache",
                        },
                        ...options
                    })

                    // Store successful GET responses in cache
                    if (useCache && (!options.method || options.method === "GET")) {
                        this.apiCache.set(cacheKey, response.data)
                    }

                    return response.data
                } catch (error: any) {
                                        
                    // Handle axios errors
                    if (error.response) {
                        // Token might be expired
                        if (error.response.status === 401) {
                            this.logout()
                            throw new Error("Session expired. Please login again.")
                        }

                        // Check for too many attempts
                        if (error.response.status === 429) {
                            console.warn(`Rate limit exceeded for ${url}. Adding cooldown.`)
                            throw new Error("Too many attempts. Please try again later.")
                        }

                        const errorMessage = error.response.data?.message || "Request failed"
                        throw new Error(errorMessage)
                    }
                    
                    throw error
                }
            },
            1,
        )
    }

    /**
     * Get a user by their ID
     * @param userId - The ID of the user to retrieve
     * @returns Promise with the user data or null if not found
     */
    async getUserById(userId: string | number): Promise<User | null> {
        // Skip invalid ID requests
        if (!userId || userId === "0" || userId === 0) {
            return null
        }

        const id = userId.toString()

        try {
            // If current user has this ID, return from local storage
            const currentUser = this.getUser()
            if (currentUser && currentUser.id && currentUser.id.toString() === id) {
                return currentUser
            }

            // Use cache key for this specific user
            const cacheKey = `user-${id}`
            const cachedUser = this.apiCache.get(cacheKey)
            if (cachedUser) {
                return cachedUser
            }

            // Otherwise fetch from API using axios instance
            const response = await axiosInstance.get(`/users/${id}`)

            if (response.data && response.data.data) {
                // Ensure the response has all required fields to match User interface
                const userData: User = {
                    id: response.data.data.id.toString(),
                    name: response.data.data.name || `User ${id}`,
                    username: response.data.data.username || `user_${id}`,
                    email: response.data.data.email || `user${id}@example.com`,
                    role: response.data.data.role,
                }

                // Cache the result
                this.apiCache.set(cacheKey, userData)
                return userData
            }

            return null
        } catch (error) {
                        return null
        }
    }

    /**
     * Get a formatted user name for display purposes
     * @param userId - The ID of the user
     * @returns A string with user name or default text
     */
    async getFormattedUserName(userId: string | number): Promise<string> {
        try {
            const user = await this.getUserById(userId)
            if (user && user.name) {
                return user.name
            } else if (user && user.username) {
                return user.username
            }
            return `User ${userId}`
        } catch (error) {
                        return `User ${userId}`
        }
    }

    /**
     * Get the role of the current user from the token
     * @returns The role string or null if not found
     */
    getRoleFromToken(): string | null {
        const token = this.getToken()
        if (!token) return null

        const decoded = this.decodeToken(token)
        if (decoded && decoded.role) {
            return decoded.role
        }

        // Try to get role from user object if not in token
        const user = this.getUser()
        return user && user.role ? user.role : null
    }

    /**
     * Get the base route path for the current user based on their role
     * @returns The base route path (e.g., '/admin', '/comptable')
     */
    getBaseRouteForRole(): string {
        const role = this.getRoleFromToken()

        // Map roles to their respective base routes
        switch (role) {
            case "admin":
            case "manager":
                return "/admin"
            case "comptable":
                return "/comptable"
            case "caissier":
                return "/cashier"
            default:
                return "/admin" // Default fallback
        }
    }
}

// Create and export a singleton instance
const authService = new AuthService()
export default authService